<template>
  <div>
    <div v-if="featureCheckState.matches('loading')" class="content">
      <Loader label="Loading Campaign..." />
    </div>

    <div v-if="!featureCheckState.matches('loading')" class="text-center">
      <div class="content">
        <h1 class="text-center text-lg sm:text-xl md:text-2xl mb-0">
          Download Newsletter
        </h1>

        <p
          class="mt-6 text-center mb-0 text-sm max-w-sm mx-auto font-medium text-support-lighter"
        >
          Get ready to download your amazing newsletter. Import it into your
          selected email service provider. Then send away 🚀
        </p>

        <DownloadSenders
          ref="downloadSenders"
          :downloading="downloading"
          :other-value="otherValue"
          :selector-value="selectorValue"
          class="mt-4 md:mt-6"
          @selector-value="selectorValue = $event"
          @other-value="otherValue = $event"
        />

        <DownloadImageHosting
          ref="imageHostingOptions"
          v-model="imageHosting"
          :downloading="downloading"
          class="mt-6 md:mt-8"
          :upgrade-required="upgradeRequired"
          :required-package="requiredPackage"
        />

        <Error
          v-if="featureCheckState.matches('error')"
          :errors="featureCheckErrors"
          :error-action="onRetryFeatureCheck"
          error-action-label="Retry?"
          message="We couldn't check the status of this feature. This might be a paid feature, please click retry to check. Alternatively continue with your download to see if it still works."
          class="mb-8 md:mb-10 md:mt-4 lg:mt-6 max-w-sm mx-auto"
        />

        <Button
          class="secondary mx-auto mt-6 md:mt-8"
          :loading="downloading"
          @click="onDownloadClick"
          >{{ downloading ? "Downloading" : "Download Now" }}</Button
        >
      </div>
    </div>
  </div>
</template>

<script>
import { useMachine } from "@xstate/vue";
import { computed } from "@vue/composition-api";
import { DownloadCampaign } from "@/graphql/mutations";
import featureCheckMachine from "@/machines/feature-check-machine";
import DownloadImageHosting from "@/components/Campaigns/DownloadCampaign/DownloadImageHosting";
import DownloadSenders from "@/components/Campaigns/DownloadCampaign/DownloadSenders";
import Error from "@/components/UI/Error";
import Loader from "@/components/UI/Loader";
import { getAccountId } from "@/lib/auth";

export default {
  name: "DownloadCampaign",
  components: { DownloadImageHosting, DownloadSenders, Error, Loader },
  setup() {
    // Initiate the machine...
    const {
      state: featureCheckState,
      send: sendToFeatureCheckService,
    } = useMachine(featureCheckMachine, {
      context: {
        feature: "imageHosting",
      },
    });

    const featureCheckErrors = computed(
      () => featureCheckState.value?.context?.error
    );

    const requiredPackage = computed(
      () => featureCheckState.value?.context?.requiredPackage
    );

    return {
      featureCheckErrors,
      featureCheckState,
      requiredPackage,
      sendToFeatureCheckService,
    };
  },
  props: {
    campaignId: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      downloading: false,
      errors: [],
      imageHosting: true,
      highlightImageHostingField: "",
      highlightSenderField: "",
      otherValue: "",
      selectorValue: null,
    };
  },
  computed: {
    exportType() {
      return this.selectorValue?.value || this.otherValue;
    },
    selectedValue() {
      return this.selectorValue?.value || this.otherValue;
    },
    upgradeRequired() {
      return (
        this.featureCheckState?.matches("upgradeRequired") && this.imageHosting
      );
    },
  },
  methods: {
    async downloadCampaign() {
      this.downloading = true;
      this.errors = [];

      try {
        const result = await this.$apollo.mutate({
          // Query
          mutation: DownloadCampaign,
          // Parameters
          variables: {
            input: {
              campaignId: this.campaignId,
              exportType: this.exportType,
              imageHosting: this.imageHosting,
            },
          },
        });

        const downloadUrl = result?.data?.downloadCampaign?.downloadUrl;

        if (downloadUrl) {
          this.handleDownloadResponse(result.data.downloadCampaign.downloadUrl);

          const gtmEvent = this.imageHosting
            ? "promo-download-with-image-hosting"
            : "promo-download-without-image-hosting";

          this.$gtm.trackEvent({
            event: gtmEvent,
            accountId: getAccountId(),
            campaignId: this.campaignId,
            exportType: this.exportType,
            paidAccount: !this.upgradeRequired,
          });

          this.$modal.hide("download-campaign");
        } else {
          Error("No data returned for download campaign response");
        }
      } catch (error) {
        this.errors.push(
          "It looks like we couldn't create the download for your campaign. Please try again. If you continue to have problems contact us at support@promo.ai"
        );
        window.Sentry && window.Sentry.captureException(error);
      } finally {
        this.downloading = false;
      }
    },
    handleDownloadResponse(downloadUrl) {
      this.$router.push({ name: "Campaigns", params: { downloadUrl } });
    },
    highlightField(ref) {
      this.$refs[ref].$el.classList.add("animate__animated", "animate__shakeX");

      return setTimeout(() => {
        this.$refs[ref].$el.classList.remove(
          "animate__animated",
          "animate__shakeX"
        );
      }, 1000);
    },
    onDownloadClick() {
      if (!this.selectedValue) {
        return this.highlightField("downloadSenders");
      }

      if (this.upgradeRequired) {
        return this.highlightField("imageHostingOptions");
      }

      this.downloadCampaign();
    },
    onRetryFeatureCheck() {
      this.sendToFeatureCheckService("RETRY");
    },
  },
};
</script>

<style scoped>
.content {
  @apply px-2 py-3;

  @screen md {
    @apply p-8;
  }
}
</style>
